import { Link } from 'react-router-dom';
import type { RecommendedRadio } from '@/mocks/recommendations';

function formatListeners(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

interface RecommendationSectionProps {
  recommendations: RecommendedRadio[];
  title?: string;
  subtitle?: string;
  compact?: boolean;
}

function RecommendationCard({ item }: { item: RecommendedRadio }) {
  const { station, reason } = item;

  return (
    <Link
      to={`/radio/${station.slug}`}
      className="group bg-background-50 rounded-2xl p-4 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
    >
      {/* Header: Logo + Info */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-xl overflow-hidden bg-background-100 shrink-0 flex items-center justify-center border border-background-200/70 relative">
          <img
            src={station.logoUrl}
            alt={station.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {station.isLive && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-accent-500 rounded-full border-2 border-background-50 animate-pulse"></span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-foreground-950 text-sm truncate group-hover:text-primary-600 transition-colors">
            {station.name}
          </h4>
          <p className="text-foreground-500 text-xs truncate">{station.city}, {station.state}</p>
        </div>
      </div>

      {/* Frequency + Category */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-baseline gap-1">
          <span className="text-primary-600 font-bold text-lg tabular-nums">{station.frequency}</span>
          <span className="text-primary-400 font-semibold text-[10px]">WEB</span>
        </div>
        <span className="text-[10px] font-medium bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">
          {station.category}
        </span>
      </div>

      {/* Reason badge */}
      <div className="flex items-center gap-1.5 mt-auto pt-3 border-t border-background-100">
        <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
          <i className="ri-sparkling-line text-accent-600 text-xs"></i>
        </div>
        <span className="text-foreground-500 text-[11px] leading-tight">{reason}</span>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3 mt-2 text-foreground-400 text-[11px]">
        <span className="flex items-center gap-1">
          <i className="ri-headphone-line text-xs"></i>
          {formatListeners(station.listeners)}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-star-fill text-yellow-500 text-xs"></i>
          {station.rating}
        </span>
        {station.isLive && (
          <span className="flex items-center gap-1 text-accent-600 font-semibold ml-auto">
            <span className="w-1.5 h-1.5 bg-accent-500 rounded-full"></span>
            EN VIVO
          </span>
        )}
      </div>
    </Link>
  );
}

export default function RecommendationSection({
  recommendations,
  title = 'También te puede gustar',
  subtitle,
  compact = false,
}: RecommendationSectionProps) {
  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  const cols = compact
    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Section header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
            <i className="ri-sparkling-line text-xs"></i>
            Descubrimiento
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="text-foreground-500 text-sm mt-2 max-w-xl">{subtitle}</p>
          )}
        </div>

        {/* Grid */}
        <div className={`grid ${cols} gap-4 md:gap-5`}>
          {recommendations.map((item) => (
            <RecommendationCard key={item.station.id} item={item} />
          ))}
        </div>

        {/* Footer link */}
        <div className="mt-8 text-center">
          <Link
            to="/radios"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold text-sm transition-colors cursor-pointer"
          >
            Explorar todas las radios
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}